import { useBonusAuditEvents, useInstanceAuditEvents } from '@/hooks/use-bonuses'

interface AuditEventsListProps {
  bonusId?: string
  instanceId?: string
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  created: 'Bonus Created',
  achieved: 'Goal Achieved',
  dispatch_initiated: 'Dispatch Started',
  dispatch_sent: 'Request Sent',
  dispatch_accepted: 'HR Accepted',
  dispatch_rejected: 'HR Rejected',
  dispatch_failed: 'Dispatch Failed',
  manually_marked_paid: 'Manually Marked Paid',
  reconciled: 'Reconciled',
}

export function AuditEventsList({ bonusId, instanceId }: AuditEventsListProps) {
  // Use instance hook if instanceId provided, otherwise use legacy bonusId hook
  const bonusQuery = useBonusAuditEvents(bonusId || '', !!bonusId && !instanceId)
  const instanceQuery = useInstanceAuditEvents(instanceId || '', !!instanceId)

  const { data, isLoading } = instanceId ? instanceQuery : bonusQuery

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-gray-100 rounded" />
  }

  if (!data?.events.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No audit events recorded
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Audit Trail</h4>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Event</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Details</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.events.map((event) => (
              <tr key={event.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-900">
                  {EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                </td>
                <td className="px-3 py-2 text-gray-600 text-xs">
                  {event.reason || '—'}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {event.previous_status && (
                    <>
                      <span className="text-gray-400">{event.previous_status}</span>
                      <span className="mx-1">→</span>
                    </>
                  )}
                  <span>{event.new_status || '—'}</span>
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                  {new Date(event.created_at * 1000).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
